import { useState } from "react";
import "./CalificacionExperiencia.css";
import { useParams } from "react-router-dom";
import { useUser } from "../../../hooks/useUser";
import { sendFeedBack } from "../../../helpers/sendFeedback";
import { SuccessMessage } from "./FeedbackResponseModal";
import parse from "html-react-parser";
import { usePublication } from "../../../hooks/usePublication";
import { useNotification } from "../../../context/notification/notificationContext";

// Definir los tipos de problemas para cada rango de calificación
type LowRatingProblem =
  | "Falta de especificidad en los contenidos"
  | "Falta de ejemplos prácticos"
  | "Contenido poco valioso frente a lo prometido"
  | "No corresponde con el título o el objetivo del recurso"
  | "Problemas técnicos durante el uso"
  | "Mejoras en navegación o usabilidad";

type MediumRatingProblem =
  | "Recurso aplicable al contexto"
  | "Mejorar alineación con resultados de aprendizaje"
  | "Necesidad de actualización de contenidos"
  | "Integración con otras herramientas académicas";

type HighRatingProblem =
  | "Alta calidad del contenido"
  | "Aplicabilidad directa en el aula"
  | "Claridad y estructura de los escenarios"
  | "Alineación con estándares académicos"
  | "Ahorro de tiempo en preparación de clases"
  | "Valor diferencial frente a otras plataformas";

type Problem = LowRatingProblem | MediumRatingProblem | HighRatingProblem;

interface CalificaTuExperienciaProps {
  onClose: () => void;
  setShowNotification: (show: boolean) => void;
}

type SubmitStatus = "idle" | "loading" | "success" | "error";

export default function CalificaTuExperiencia({
  onClose,
  setShowNotification,
}: CalificaTuExperienciaProps): JSX.Element {
  const [rating, setRating] = useState<number | null>(null);
  const [issues, setIssues] = useState<Problem[]>([]);
  const [comment, setComment] = useState<string>("");
  const [isClosing, setIsClosing] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");
  const [submitError, setSubmitError] = useState<string>("");
  const { user } = useUser();
  const { publication } = usePublication();
  let { idRevista, idLibro } = useParams();
  const { triggerNotification } = useNotification();

  // Función para obtener los problemas según la calificación
  const getProblemsByRating = (rating: number): Problem[] => {
    if (rating >= 1 && rating <= 6) {
      return [
        "Falta de especificidad en los contenidos",
        "Falta de ejemplos prácticos",
        "Contenido poco valioso frente a lo prometido",
        "No corresponde con el título o el objetivo del recurso",
        "Problemas técnicos durante el uso",
        "Mejoras en navegación o usabilidad",
      ];
    } else if (rating >= 7 && rating <= 8) {
      return [
        "Recurso aplicable al contexto",
        "Mejorar alineación con resultados de aprendizaje",
        "Necesidad de actualización de contenidos",
        "Integración con otras herramientas académicas",
      ];
    } else if (rating >= 9 && rating <= 10) {
      return [
        "Alta calidad del contenido",
        "Aplicabilidad directa en el aula",
        "Claridad y estructura de los escenarios",
        "Alineación con estándares académicos",
        "Ahorro de tiempo en preparación de clases",
        "Valor diferencial frente a otras plataformas",
      ];
    }
    return [];
  };

  const toggleIssue = (problem: Problem): void => {
    setIssues((prev) =>
      prev.includes(problem)
        ? prev.filter((p) => p !== problem)
        : [...prev, problem]
    );
  };

  const handleSubmit = async (): Promise<void> => {
    // Validar que se haya seleccionado un rating
    if (rating === null) {
      setSubmitError("Por favor selecciona una calificación antes de enviar");
      return;
    }

    // Limpiar error previo
    setSubmitError("");
    setSubmitStatus("loading");

    try {
      if (rating !== null && (idRevista || idLibro)) {
        const issuesString = issues.length > 0 ? issues.join("; ") : "";
        let nombre = "";
        let tipo = "";
        let bv = "";

        if (idLibro) {
          nombre = `Libro_${idLibro}: ${publication.title}`;
          tipo = "lib";
          const url = publication.urlVirtualpro;
          bv = url?.split("-").pop() || "";
        }

        if (idRevista) {
          nombre = `Revista_${idRevista}: ${publication.title}`;
            tipo = "rev";
        }

        await sendFeedBack({
          rating,
          comment,
          infographicId: idRevista || idLibro || "",
          infographicName: nombre,
          id_usuario: user?.sus_id,
          nombre_usuario: user?.sus_nombre,
          email: user?.sus_email,
          issues: issuesString,
          modulo: tipo,
          bv
        });

        setSubmitStatus("success");
      }
    } catch (error) {
      setSubmitStatus("error");
      setSubmitError(
        "Hubo un problema al enviar tu calificación. Por favor, intenta nuevamente."
      );
      console.error("Error al enviar feedback:", error);
    }
  };

  // Función para manejar el cierre con animación
  const handleClose = () => {
    setIsClosing(true);
    // Esperar a que termine la animación antes de llamar a onClose
    setTimeout(() => {
      onClose();
    }, 400);
  };

  // Función para manejar la selección de rating
  const handleRatingSelect = (num: number) => {
    // Cuando cambia la calificación, limpiar los issues y cargar los nuevos según la calificación
    setRating(num);
    setIssues([]); // Limpiar issues anteriores

    if (submitError) {
      setSubmitError("");
    }
  };

  // Función para reintentar el envío
  const handleRetry = () => {
    setSubmitStatus("idle");
    setSubmitError("");
    handleSubmit();
  };

  const isLoading = submitStatus === "loading";
  const isSuccess = submitStatus === "success";
  const isError = submitStatus === "error";

  // Obtener problemas actuales según la calificación seleccionada
  const currentProblems = rating !== null ? getProblemsByRating(rating) : [];

  return (
    <>
      {/* Modal de calificación original - solo se muestra si no está en estado success */}
      {!isSuccess && (
        <div className={`overlay-experiencia ${isClosing ? "closing" : ""}`}>
          <div className={`modal-experiencia ${isClosing ? "closing" : ""}`}>
            <button
              className="close-experiencia"
              aria-label="Cerrar modal"
              onClick={() => {
                handleClose();
                setTimeout(() => {
                  triggerNotification('help-button');
                  setShowNotification(true);
                }, 300);
              }}
              disabled={isLoading}
            >
              ×
            </button>

            <h2 className="title-experiencia">Califica tu experiencia</h2>
            <p className="subtitle-experiencia">
              Ayúdanos a mejorar calificando tu experiencia usando Infografías
            </p>

            {/* Rating */}
            <div className="rating-experiencia">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  type="button"
                  className={`rate-circle-experiencia ${
                    rating === num
                      ? num === 2
                        ? "rate-active-green-experiencia"
                        : "rate-active-experiencia"
                      : ""
                  }`}
                  onClick={() => handleRatingSelect(num)}
                  disabled={isLoading}
                >
                  {num}
                </button>
              ))}
            </div>

            {/* Mensaje de error de validación */}
            {submitError && !isLoading && (
              <div className="error-alert-experiencia">
                <span className="error-alert-experiencia__icon">
                  <i className="icon icon-vpro-oc-info"></i>
                </span>
                <span className="error-alert-experiencia__text">
                  {submitError}
                </span>
                {isError && (
                  <button
                    type="button"
                    className="error-retry-button-experiencia"
                    onClick={handleRetry}
                  >
                    Reintentar
                  </button>
                )}
              </div>
            )}

            {/* Indicador de carga */}
            {isLoading && (
              <div className="loading-indicator-experiencia">
                <div className="loading-spinner-experiencia"></div>
                <p className="loading-text-experiencia">
                  Enviando calificación...
                </p>
              </div>
            )}

            {/* Problemas (siempre visibles cuando hay una calificación) */}
            {rating !== null && !isLoading && (
              <div className="section-experiencia">
                <h3 className="section-title-experiencia">
                  {rating <= 6
                    ? "¿Qué problema encontraste?"
                    : rating <= 8
                    ? "¿Qué seguirías mejorando?"
                    : "¿Qué fue lo más valioso?"}
                </h3>
                <div className="chips-experiencia">
                  {currentProblems.map((problem) => (
                    <button
                      key={problem}
                      type="button"
                      className={`chip-experiencia ${
                        issues.includes(problem)
                          ? "chip-active-experiencia"
                          : ""
                      }`}
                      onClick={() => toggleIssue(problem)}
                      disabled={isLoading}
                    >
                      {problem}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Comentario */}
            {!isLoading && (
              <div className="section-experiencia">
                <h3 className="section-title-experiencia">
                  Deja un comentario
                </h3>
                <textarea
                  className="textarea-experiencia"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Escribe tus comentarios aquí..."
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Acciones */}
            <div className="actions-experiencia">
              <button
                type="button"
                className="btn-secondary-experiencia"
                onClick={() => {
                  handleClose();
                  setTimeout(() => {
                    triggerNotification('help-button');
                    setShowNotification(true);
                  }, 300);
                }}
                disabled={isLoading}
              >
                En otro momento
              </button>
              <button
                type="button"
                className="btn-primary-experiencia"
                onClick={handleSubmit}
                disabled={isLoading || rating === null}
              >
                {isLoading ? "Enviando calificación..." : "Enviar calificación"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de éxito separado - se renderiza solo cuando isSuccess es true */}
      {isSuccess && rating !== null && (
        <SuccessMessage rating={rating} onClose={onClose} />
      )}
    </>
  );
}
